import { useEffect, useRef, type ReactNode } from 'react'
import { useI18n } from '../i18n'

export function Spinner({ className = '' }: { className?: string }) {
  return <div className={`h-6 w-6 animate-spin rounded-full border-2 border-brand-300 border-t-brand-700 ${className}`} />
}

export function PageSpinner() {
  return (
    <div className="grid min-h-[50vh] place-items-center">
      <Spinner className="h-8 w-8" />
    </div>
  )
}

export function Input({
  label, value, onChange, type = 'text', placeholder, required, onBlur,
}: {
  label?: string; value: string; onChange: (v: string) => void
  type?: string; placeholder?: string; required?: boolean; onBlur?: () => void
}) {
  return (
    <label className="block">
      {label && (
        <span className="mb-1 block text-sm font-medium text-brand-700 dark:text-brand-200">
          {label} {required && <span className="text-brand-400">*</span>}
        </span>
      )}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-4 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-300/40"
      />
    </label>
  )
}

export function Textarea({
  label, value, onChange, placeholder, rows = 4,
}: { label?: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-sm font-medium text-brand-700 dark:text-brand-200">{label}</span>}
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-4 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-300/40"
      />
    </label>
  )
}

export function Select({
  label, value, onChange, options,
}: { label?: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-sm font-medium text-brand-700 dark:text-brand-200">{label}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-4 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-300/40"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const { t } = useI18n()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose()
      if (e.key !== 'Tab') return
      const f = ref.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      )
      if (!f || !f.length) return
      const first = f[0]
      const last = f[f.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.() }
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-label={t('common.close')} tabIndex={-1} onClick={onClose} />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-brand-50 dark:bg-brand-900 p-6 shadow-lift outline-none"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} aria-label={t('common.close')} className="grid h-8 w-8 place-items-center rounded-full hover:bg-brand-100 dark:hover:bg-brand-800">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function EmptyState({ icon, title, body, action }: { icon: string; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="card grid place-items-center px-6 py-12 text-center">
      <div className="text-4xl">{icon}</div>
      <h3 className="mt-3 font-bold">{title}</h3>
      {body && <p className="mt-1 max-w-sm text-sm text-brand-600 dark:text-brand-300">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}
